import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  CourierInvoiceDisputeDto,
  CreditNoteDto,
  DomainMetaDataDto,
  EntityActivityLogDto,
  GroupedInvoiceFileProcessingStatusDto,
  InvoiceAttachmentUploadSignedUrlRequestDto,
  InvoiceCourierShipmentDetailDto,
  InvoiceDto,
  InvoiceMetadataResponseDto,
  InvoiceSearchMetadataDto,
  InvoiceSearchRequestDto,
  InvoiceSummaryDto,
  InvoiceUploadSignedUrlRequestDto,
  KeyMetricsResponseDto,
  ResponseDtoListCurrencyEnum,
  RouteDto,
  ShipmentInActionDetailDto,
  ShipmentMessageDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { ITableData, ITableParams } from 'src/types/table';
import { CacheKeys, cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { invoiceSearchResponseToTableData, tableFiltersToInvoiceDashboardFilters, tableParamsToInvoiceSearchRequest } from 'src/utils/invoiceMapper';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { useParams } from 'react-router-dom';

const NO_CACHE_QUERY_OPTIONS = {
  cacheTime: 0,
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  keepPreviousData: false,
  retry: 1,
} as const;

export const invalidateShipmentCaches = (queryClient: ReturnType<typeof useQueryClient>, invoiceId?: string) =>
  Promise.all([
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.getShipment] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.invoiceSummary] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.approvalUserResponse] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.invoices] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.invoiceStats] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.serviceProvider] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.getActivityLogs] }),
    queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.activeNoContractDraft] }),
    ...(invoiceId
      ? [queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)), queryClient.invalidateQueries(cacheKeysUtil.getProcessingStages(invoiceId))]
      : [
          queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.getVersion] }),
          queryClient.invalidateQueries({ exact: false, queryKey: [CacheKeys.getProcessingStages] }),
        ]),
  ]);

/**
 * @deprecated Use useCombinedInvoiceSearch instead for better performance
 * This hook will be removed in a future version
 */

export const useInvoiceList = (tableParams: ITableParams = {}): UseQueryResult<ITableData> =>
  useQuery(
    // Create a completely unique query key for each currency to prevent any cache sharing
    ['invoices', 'currency', tableParams?.filters?.currencyReference, 'params', JSON.stringify(tableParamsToInvoiceSearchRequest(tableParams))],
    async (): Promise<ITableData> => {
      const apiRequest = tableParamsToInvoiceSearchRequest(tableParams);
      const apiResponse = await tFinancialService.api.getInvoiceList(apiRequest);
      const tableData = invoiceSearchResponseToTableData(apiResponse.data);

      return tableData;
    },
    {
      enabled: !!tableParams?.filters?.currencyReference,
      ...NO_CACHE_QUERY_OPTIONS,
    }
  );
/**
 * NEW: Phase 1 - Fetch invoice basic data
 * Calls /api/invoices/search/invoice-data
 * Returns basic invoice info for faster initial display
 */
export const useInvoiceDataSearch = (tableParams: ITableParams = {}): UseQueryResult<ITableData> =>
  useQuery(
    ['invoices-data', 'currency', tableParams?.filters?.currencyReference, 'params', JSON.stringify(tableParamsToInvoiceSearchRequest(tableParams))],
    async (): Promise<ITableData> => {
      const apiRequest = tableParamsToInvoiceSearchRequest(tableParams);
      const apiResponse = await tFinancialService.api.searchInvoiceData(apiRequest);
      const tableData = invoiceSearchResponseToTableData(apiResponse.data);

      return tableData;
    },
    {
      enabled: !!tableParams?.filters?.currencyReference,
      ...NO_CACHE_QUERY_OPTIONS,
    }
  );

/**
 * NEW: Phase 2 - Fetch charge data
 * Calls /api/invoices/search/charge-data
 * Returns: deviationAmount, invoiceStatusOverview (pending items)
 * Requires invoiceIds from Phase 1
 */
export const useInvoiceChargeDataSearch = (tableParams: ITableParams = {}, invoiceIds: string[] = []): UseQueryResult<{ items: any[] }> =>
  useQuery(
    ['invoices-charge-data', 'currency', tableParams?.filters?.currencyReference, 'invoiceIds', JSON.stringify(invoiceIds)],
    async (): Promise<{ items: any[] }> => {
      const apiRequest = {
        ...tableParamsToInvoiceSearchRequest(tableParams),
        invoiceId: invoiceIds, // Add invoiceIds to the request
      };
      const apiResponse = await tFinancialService.api.searchInvoiceChargeData(apiRequest);

      return { items: apiResponse.data.data || [] };
    },
    {
      enabled: !!tableParams?.filters?.currencyReference && invoiceIds.length > 0,
      ...NO_CACHE_QUERY_OPTIONS,
    }
  );

/**
 * NEW: Combined hook - Orchestrates sequential API calls
 * 1. Fetches invoice data first (fast) - most fields
 * 2. Extracts invoiceIds
 * 3. Fetches charge data (deviationAmount & invoiceStatusOverview)
 * 4. Merges both datasets
 */
export const useCombinedInvoiceSearch = (
  tableParams: ITableParams = {}
): UseQueryResult<ITableData> & {
  isLoadingInvoiceData: boolean;
  isLoadingChargeData: boolean;
} => {
  // Phase 1: Fetch invoice data
  const { data: invoiceData, isLoading: isLoadingInvoiceData, refetch: refetchInvoiceData } = useInvoiceDataSearch(tableParams);

  // Extract invoiceIds from invoice data
  const invoiceIds = invoiceData?.items?.map((item) => item.id).filter(Boolean) || [];

  // Phase 2: Fetch charge data (only when we have invoiceIds)
  const { data: chargeData, isLoading: isLoadingChargeData, refetch: refetchChargeData } = useInvoiceChargeDataSearch(tableParams, invoiceIds);

  // Merge invoice data with charge data
  const mergedData: ITableData = {
    items: invoiceData?.items || [],
    total: invoiceData?.total || 0,
    stats: invoiceData?.stats || {},
    allowedCurrencies: invoiceData?.allowedCurrencies || [],
  };

  // If charge data is available, merge it
  // IMPORTANT: Merge charge-specific fields from /api/invoices/search/charge-data
  // These fields are: deviationAmount, invoiceStatusOverview (pending items)
  if (chargeData?.items && chargeData.items.length > 0) {
    mergedData.items = mergedData.items.map((invoice) => {
      const chargeInfo = chargeData.items.find((charge) => charge.id === invoice.id);
      if (chargeInfo) {
        // Merge charge data fields into invoice
        // Only override if charge data has actual values (not null/undefined)
        return {
          ...invoice,
          // Deviation Amount - from charge API
          ...(chargeInfo.deviationAmount !== undefined && chargeInfo.deviationAmount !== null ? { deviationAmount: chargeInfo.deviationAmount } : {}),
          // Invoice Status Overview (Pending Items) - from charge API
          ...(chargeInfo.invoiceStatusOverview !== undefined && chargeInfo.invoiceStatusOverview !== null
            ? { invoiceStatusOverview: chargeInfo.invoiceStatusOverview }
            : {}),
          // Add any other charge-specific fields here as the API evolves
        };
      }
      return invoice;
    });
  }

  // Combined refetch function
  const refetch = async () => {
    await refetchInvoiceData();
    if (invoiceIds.length > 0) {
      await refetchChargeData();
    }
  };

  return {
    data: mergedData,
    isLoading: isLoadingInvoiceData, // Show loading based on invoice data (primary)
    isLoadingInvoiceData,
    isLoadingChargeData,
    refetch,
    // Pass through other react-query properties
    isError: false,
    error: null,
    isSuccess: !!invoiceData,
    status: isLoadingInvoiceData ? 'loading' : 'success',
  } as any;
};

/**
 * DEPRECATED: Use useCombinedInvoiceStats instead
 * Legacy hook for backward compatibility
 */
export const useInvoiceStats = (invoiceNumber: string, invoiceDashboardFilters: Record<string, string> = {}): UseQueryResult<KeyMetricsResponseDto> => {
  const filter = {
    ...tableFiltersToInvoiceDashboardFilters(invoiceDashboardFilters),
    invoiceNumber,
    granularity: 'MONTHLY' as const, // Required by AnalyticSearchRequestDto
  };
  return useQuery(
    cacheKeysUtil.invoiceStats(filter),
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsWithFilter('DASHBOARD', filter)).data.data,
    { enabled: !!filter.currencyReference }
  );
};

/**
 * NEW: Phase 1 - Fetch invoice metrics (fast)
 * Calls /api/key-metrics/invoice-data
 * Returns: totalAmount, noShipments, noInvoices
 */
export const useInvoiceStatsData = (invoiceNumber: string, invoiceDashboardFilters: Record<string, string> = {}): UseQueryResult<KeyMetricsResponseDto> => {
  const filter = {
    ...tableFiltersToInvoiceDashboardFilters(invoiceDashboardFilters),
    invoiceNumber: invoiceNumber ? [invoiceNumber] : undefined, // Convert string to array
  };
  const searchFilters = filter as InvoiceSearchRequestDto;
  searchFilters.searchFilters = filter.filters;
  return useQuery(
    ['invoice-stats-data', searchFilters],
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsAuditInvoiceData(searchFilters)).data.data,
    {
      enabled: !!searchFilters.currencyReference,
      ...NO_CACHE_QUERY_OPTIONS,
    }
  );
};

/**
 * NEW: Phase 2 - Fetch charge metrics (slower)
 * Calls /api/key-metrics/charge-data
 * Returns: totalDeviationAmount, noCharges
 */
export const useInvoiceStatsChargeData = (
  invoiceNumber: string,
  invoiceDashboardFilters: Record<string, string> = {},
  enabled: boolean = true
): UseQueryResult<KeyMetricsResponseDto> => {
  const filter = {
    ...tableFiltersToInvoiceDashboardFilters(invoiceDashboardFilters),
    invoiceNumber: invoiceNumber ? [invoiceNumber] : undefined, // Convert string to array
  };
  const searchFilters = filter as InvoiceSearchRequestDto;
  searchFilters.searchFilters = filter.filters;
  return useQuery(
    ['invoice-stats-charge-data', searchFilters],
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsAuditChargeData(searchFilters)).data.data,
    {
      enabled: enabled && !!searchFilters.currencyReference,
      ...NO_CACHE_QUERY_OPTIONS,
    }
  );
};

/**
 * NEW: Combined hook - Orchestrates sequential API calls for key metrics
 * 1. Fetches invoice stats first (Phase 1 - fast)
 * 2. Fetches charge stats (Phase 2 - background)
 * 3. Merges both datasets
 */
export const useCombinedInvoiceStats = (
  invoiceNumber: string,
  invoiceDashboardFilters: Record<string, string> = {}
): UseQueryResult<KeyMetricsResponseDto> & {
  isLoadingInvoiceStats: boolean;
  isLoadingChargeStats: boolean;
} => {
  // Phase 1: Fetch invoice stats
  const {
    data: invoiceStatsData,
    isLoading: isLoadingInvoiceStats,
    refetch: refetchInvoiceStats,
  } = useInvoiceStatsData(invoiceNumber, invoiceDashboardFilters);

  // Phase 2: Fetch charge stats (only after Phase 1 completes)
  const {
    data: chargeStatsData,
    isLoading: isLoadingChargeStats,
    refetch: refetchChargeStats,
  } = useInvoiceStatsChargeData(invoiceNumber, invoiceDashboardFilters, !!invoiceStatsData);

  // Merge both datasets
  const mergedData: KeyMetricsResponseDto = {
    keymetrics: {
      // Phase 1 data (invoice-data endpoint)
      totalAmount: invoiceStatsData?.keymetrics?.totalAmount,
      noShipments: invoiceStatsData?.keymetrics?.noShipments,
      noInvoices: invoiceStatsData?.keymetrics?.noInvoices,
      totalCo2Emissions: invoiceStatsData?.keymetrics?.totalCo2Emissions,
      nrcfShipmentCount: invoiceStatsData?.keymetrics?.nrcfShipmentCount,

      // Phase 2 data (charge-data endpoint)
      noCharges: chargeStatsData?.keymetrics?.noCharges,
      nrcfShipmentAmount: chargeStatsData?.keymetrics?.nrcfShipmentAmount,
      totalDeviationAmount: chargeStatsData?.keymetrics?.totalDeviationAmount,
      noDeviations: chargeStatsData?.keymetrics?.noDeviations,
      unitPriceDeviationCount: chargeStatsData?.keymetrics?.unitPriceDeviationCount,
      unitPriceDeviationAmount: chargeStatsData?.keymetrics?.unitPriceDeviationAmount,
      quantityDeviationCount: chargeStatsData?.keymetrics?.quantityDeviationCount,
      quantityDeviationAmount: chargeStatsData?.keymetrics?.quantityDeviationAmount,
      unknownChargeDeviationCount: chargeStatsData?.keymetrics?.unknownChargeDeviationCount,
      unknownChargeDeviationAmount: chargeStatsData?.keymetrics?.unknownChargeDeviationAmount,
      serviceTypeApprovalCount: chargeStatsData?.keymetrics?.serviceTypeApprovalCount,
      accessorialApprovalCount: chargeStatsData?.keymetrics?.accessorialApprovalCount,
    },
    meta: invoiceStatsData?.meta || chargeStatsData?.meta,
  };

  // Combined refetch
  const refetch = async () => {
    await refetchInvoiceStats();
    await refetchChargeStats();
  };

  return {
    data: mergedData,
    isLoading: isLoadingInvoiceStats, // Only wait for Phase 1
    isLoadingInvoiceStats,
    isLoadingChargeStats,
    refetch,
    // Pass through other react-query properties
    isError: false,
    error: null,
    isSuccess: !!invoiceStatsData,
    status: isLoadingInvoiceStats ? 'loading' : 'success',
  } as any;
};

export const useInvoiceMetaData = (invoiceNumber: string, invoiceDashboardFilters: Record<string, any> = {}): UseQueryResult<InvoiceMetadataResponseDto> => {
  // Use the same full filter mapping as the main search API
  const tableParams = {
    filters: invoiceDashboardFilters,
    page: 1,
    pageSize: 25,
    orderBy: 'INVOICE_DATE',
    orderMethod: 'asc' as const,
    keyword: invoiceNumber,
  };
  const filter = tableParamsToInvoiceSearchRequest(tableParams);

  return useQuery(
    cacheKeysUtil.invoiceMetadata(filter),
    async (): Promise<InvoiceMetadataResponseDto> => (await tFinancialService.api.getInvoiceMetadata(filter)).data.data,
    { enabled: !!filter.currencyReference }
  );
};

// New hook to call the updated metadata endpoint for invoice search filters
export const useInvoiceSearchMetaData = (
  invoiceNumber: string,
  invoiceDashboardFilters: Record<string, any> = {}
): UseQueryResult<InvoiceSearchMetadataDto> => {
  // Use the same full filter mapping as the main search API
  const tableParams = {
    filters: invoiceDashboardFilters,
    page: 1,
    pageSize: 25,
    orderBy: 'INVOICE_DATE',
    orderMethod: 'asc' as const,
    keyword: invoiceNumber,
    currencyReference: invoiceDashboardFilters.currencyReference,
  };
  const filter = tableParamsToInvoiceSearchRequest(tableParams);

  return useQuery(
    ['invoiceSearchMetadata', filter],
    async (): Promise<InvoiceSearchMetadataDto> => (await tFinancialService.api.getInvoiceSearchMetadata(filter)).data.data,
    { enabled: !!filter.currencyReference }
  );
};

/**
 * Lazy load metadata for a single column
 * Only fetches when enabled (e.g., when filter popover opens)
 */
export const useInvoiceSearchMetaDataByColumn = (
  column: string,
  invoiceNumber: string,
  invoiceDashboardFilters: Record<string, any> = {},
  enabled: boolean = false
): UseQueryResult<any> => {
  const tableParams = {
    filters: invoiceDashboardFilters,
    page: 1,
    pageSize: 25,
    orderBy: 'INVOICE_DATE',
    orderMethod: 'asc' as const,
    keyword: invoiceNumber,
    currencyReference: invoiceDashboardFilters.currencyReference,
  };
  const filter = tableParamsToInvoiceSearchRequest(tableParams);

  return useQuery(
    ['invoiceSearchMetadata', column, filter],
    async (): Promise<any> => {
      const response = await tFinancialService.api.getInvoiceSearchMetadataByColumn(column as any, filter);
      return response.data.data;
    },
    {
      enabled: enabled && !!filter.currencyReference,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );
};

/**
 * This is for non COURIER INVOICE
 * @param invoiceId
 * @param invoiceSummary
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useInvoiceDetail = (invoiceId: string, invoiceSummary?: InvoiceSummaryDto, _isProcurement?: boolean): UseQueryResult<Record<string, any>> =>
  useQuery(
    cacheKeysUtil.actionDetails(invoiceId),
    async (): Promise<Record<string, any>> =>
      // use get shipment instead of invoice detail
      // const response = await tFinancialService.api.getInvoiceDetail(invoiceId);
      // const { data } = response;
      // data.data.deviations = _.orderBy(data.data.deviations, ({ id }) => id);
      null,
    {
      enabled: !!invoiceSummary && invoiceSummary.modality !== 'COURIER',
    }
  );

export const useSimpleInvoiceDetail = (invoiceId: string): UseQueryResult<InvoiceDto> =>
  useQuery(
    cacheKeysUtil.actionDetails(invoiceId),
    async (): Promise<InvoiceDto> =>
      // use get shipment instead of invoice detail
      // const response = await tFinancialService.api.getInvoiceDetail(invoiceId);
      // const { data } = response;
      null,
    {
      enabled: !!invoiceId,
    }
  );

export const useInvoiceDetailFromParams = () => {
  const { id } = useParams();
  return useInvoiceDetail(id);
};

export const useInvoiceSummaryFromParams = () => {
  const { id } = useParams();
  return useInvoiceSummary(id);
};

interface IPerformDeviationWorkflowActionArgs {
  invoiceId: string;
  reason?: string;
  deviationWorkflowAction:
    | 'SEND_TO_PROCUREMENT'
    | 'SEND_TO_FINANCE'
    | 'START_DISPUTE'
    | 'SEND_TO_OPERATION'
    | 'MARK_INVOICE_TO_REJECT'
    | 'MARK_INVOICE_TO_APPROVE'
    | 'ACCEPT_CREDIT_NOTE'
    | 'READY_FOR_PAYMENT'
    | 'REJECT_INVOICE'
    | 'ACCEPT_INVOICE';
}

export interface IPerformShipmentWorkflowActionArgs {
  shipmentInvoiceId: string;
  invoiceId: string;
  reason?: string;
  spotShipmentCharges?: Array<{
    id: string;
    chargeCategory: string;
    legType: string;
  }>;
  deviationWorkflowAction:
    | 'SEND_TO_PROCUREMENT'
    | 'SEND_TO_FINANCE'
    | 'START_DISPUTE'
    | 'SEND_TO_OPERATION'
    | 'MARK_INVOICE_TO_REJECT'
    | 'MARK_INVOICE_TO_APPROVE'
    | 'ACCEPT_CREDIT_NOTE'
    | 'READY_FOR_PAYMENT'
    | 'REJECT_INVOICE'
    | 'ACCEPT_INVOICE'
    | 'ACCEPT_AS_SPOT_SHIPMENT';
}

export const usePerformDeviationWorkflowAction = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ invoiceId, deviationWorkflowAction, reason }: IPerformDeviationWorkflowActionArgs) => {
      const apiData = reason ? { reason } : {};
      return tFinancialService.api.performDeviationWorkflowAction(invoiceId, deviationWorkflowAction, apiData);
    },
    {
      onSuccess: async (_, args) => {
        await Promise.all([
          queryClient.invalidateQueries(cacheKeysUtil.actionDetails(args.invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(args.invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.userResponse(args.invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.approvalUserResponse(args.invoiceId)),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoices],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoiceStats],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.serviceProvider],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getActivityLogs],
          }),
          queryClient.invalidateQueries(cacheKeysUtil.getVersion(args.invoiceId)),
        ]);
      },
    }
  );
};

export const usePerformShipmentWorkflowAction = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ shipmentInvoiceId, deviationWorkflowAction, reason, spotShipmentCharges }: IPerformShipmentWorkflowActionArgs) => {
      const apiData: any = {};
      if (reason) apiData.reason = reason;
      if (spotShipmentCharges) apiData.spotShipmentCharges = spotShipmentCharges;
      return tFinancialService.api.performDeviationWorkflowActionOnShipmentInvoice(shipmentInvoiceId, deviationWorkflowAction, apiData);
    },
    {
      onSuccess: async (data, args) => {
        if (!data?.data?.data?.asyncProcessing) {
          await invalidateShipmentCaches(queryClient, args.invoiceId);
        }
      },
    }
  );
};

export interface IConfirmShipmentInvoiceArgs {
  shipmentInvoiceId: string;
  invoiceId: string;
  reason?: string;
  spotShipmentCharges?: Array<{
    id: string;
    chargeCategory: string;
    legType: string;
  }>;
}

export const useConfirmShipmentInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ shipmentInvoiceId, reason, spotShipmentCharges }: IConfirmShipmentInvoiceArgs) => {
      const apiData: any = {};
      if (reason) apiData.reason = reason;
      if (spotShipmentCharges) apiData.spotShipmentCharges = spotShipmentCharges;
      return tFinancialService.api.confirmShipmentInvoice(shipmentInvoiceId, apiData);
    },
    {
      onSuccess: async (data, args) => {
        if (!data?.data?.data?.asyncProcessing) {
          await invalidateShipmentCaches(queryClient, args.invoiceId);
        }
      },
    }
  );
};

// DEPRECATED: This hook is maintained for backward compatibility but uses the new API internally
// For new code, prefer using useGetInvoiceFileUrls directly
export const useGetFileUrl = (invoiceId: string): UseQueryResult<string> => {
  // Use the new hook to get all file URLs
  const fileUrlsQuery = useGetInvoiceFileUrls(invoiceId);

  // Create a derived query that works with the old pattern
  return {
    ...fileUrlsQuery,
    data: fileUrlsQuery.data && fileUrlsQuery.data.length > 0 ? fileUrlsQuery.data[0].fileUrl : '',
    // Keep the same behavior for refetch
    refetch: async () => {
      const result = await fileUrlsQuery.refetch();
      return {
        ...result,
        data: result.data && result.data.length > 0 ? result.data[0].fileUrl : '',
      };
    },
  } as UseQueryResult<string>; // Type cast to maintain the old return type
};

// New hook to fetch multiple file URLs from new endpoint
export interface InvoiceFileUrl {
  fileUrl: string;
  fileName: string;
  fileType: string; // PDF, CSV, etc.
  status?: string;
}

export const useGetInvoiceFileUrls = (invoiceId: string): UseQueryResult<InvoiceFileUrl[]> =>
  useQuery(
    ['invoice', invoiceId, 'fileUrls'], // Use a unique cache key
    async (): Promise<InvoiceFileUrl[]> => {
      if (!invoiceId) return [];
      try {
        // Call the API endpoint to get multiple file URLs
        const response = await tFinancialService.api.getFileUrls(invoiceId);

        // Transform the API response to our internal format
        if (response?.data?.data) {
          return response.data.data.map((file) => {
            // Extract file extension from the file name
            const extension = file.fileName?.split('.')?.pop() || 'unknown';
            return {
              fileUrl: file.fileUrl || '',
              fileName: file.fileName || 'Unknown File',
              fileType: extension.toUpperCase(),
              status: file.status,
            };
          });
        }
        return [];
      } catch (error) {
        console.error('Error fetching invoice file URLs:', error);
        return [];
      }
    },
    { enabled: !!invoiceId }
  );

export const useGetConfiguredRateCard = (shipmentInvoiceId: string): UseQueryResult<string> =>
  useQuery(
    cacheKeysUtil.getInvoiceConfiguredRateCard(shipmentInvoiceId),
    async (): Promise<void> => (await tFinancialService.api.getConfiguredRateCard(shipmentInvoiceId, { format: 'blob' })).data,
    {
      enabled: false,
    }
  );

export const useGetFileAttachmentUrl = (invoiceId: string, attachmentId: string): UseQueryResult<string> =>
  useQuery(
    cacheKeysUtil.getFileUrl(invoiceId),
    async (): Promise<string> => (await tFinancialService.api.getFileUrlAttachment(invoiceId, attachmentId)).data.data,
    {
      enabled: false,
    }
  );

export const useGetInvoiceReport = (invoiceId: string, enabled: boolean = false): UseQueryResult<string> =>
  useQuery(
    cacheKeysUtil.getInvoiceReport(invoiceId),
    async (): Promise<void> => (await tFinancialService.api.generateInvoiceReportZip(invoiceId, { format: 'blob' })).data,
    {
      enabled,
      cacheTime: 0,
    }
  );

interface IUploadCreditNoteProps {
  invoiceId: string;
  creditNoteFileUrl: string;
}

interface IGetUploadCreditNoteURLArgs {
  disputeId: string;
  fileName: string;
}

export const useGetUploadCreditNoteURL = () =>
  useMutation(
    async ({ disputeId, fileName }: IGetUploadCreditNoteURLArgs) => (await tFinancialService.api.getUploadCreditNoteUrl1(disputeId, fileName)).data.data
  );

export const useGetCreditNoteFileURL = (disputeId: string): UseQueryResult<string> =>
  useQuery(
    cacheKeysUtil.getCreditNoteFileUrl(disputeId),
    async (): Promise<string> => (await tFinancialService.api.getCreditNoteFileUrl(disputeId)).data.data,
    {
      enabled: false,
    }
  );

export const useUpdateCreditNote = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();

  return useMutation(
    async ({ invoiceId, creditNoteFileUrl }: IUploadCreditNoteProps) => tFinancialService.api.getUploadCreditNoteUrl(invoiceId, { creditNoteFileUrl }),
    {
      onSuccess: async (_, args) => {
        await Promise.all([
          queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.getCreditNoteFileUrl(args.invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
        ]);
      },
    }
  );
};

export const useDeleteCreditNote = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();

  return useMutation(async (invoiceId: string) => tFinancialService.api.deleteCreditNotes(invoiceId), {
    onSuccess: async (_, args) => {
      await Promise.all([
        queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceId)),
        queryClient.invalidateQueries(cacheKeysUtil.getCreditNoteFileUrl(args)),
        queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
      ]);
    },
  });
};

interface IUpdateDisputeCreditNoteProps {
  disputeId: string;
  creditNoteData: CreditNoteDto;
}

export const useGetDisputeCreditNote = (disputeId: string, enabled = true): UseQueryResult<CreditNoteDto> =>
  useQuery(
    cacheKeysUtil.getDisputeCreditNote(disputeId),
    async (): Promise<CreditNoteDto> => (await tFinancialService.api.getInvoiceDisputeCreditNote(disputeId)).data.data,
    {
      enabled,
    }
  );

export const useUpdateDisputeCreditNote = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();

  return useMutation(
    async ({ disputeId, creditNoteData }: IUpdateDisputeCreditNoteProps) => tFinancialService.api.updateInvoiceDisputeCreditNote(disputeId, creditNoteData),
    {
      onSuccess: async (_, args) => {
        await Promise.all([
          queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.getDisputeCreditNote(args.disputeId)),
          queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
        ]);
      },
    }
  );
};

// First Step to upload invoice, after this execut we will get presigned list url to upload invoice
export const useUploadInvoice = () =>
  useMutation(async (data: InvoiceUploadSignedUrlRequestDto) => {
    const presignedUrls = (await tFinancialService.api.getInvoicePreSignedUrLs(data)).data.data;
    return presignedUrls;
  });

export const useNotifyFileUploaded = () =>
  useMutation(async (invoiceFileProcessingId: string) => tFinancialService.api.updateStatusInvoiceFileProcessing(invoiceFileProcessingId, 'UPLOADED_FILE', {}));

export const useInvoiceFileProcessing = (batchId: string, enabled: boolean, refetchInterval: number | false = 3000) =>
  useQuery(
    cacheKeysUtil.getInvoiceFileProcessing(batchId),
    async (): Promise<GroupedInvoiceFileProcessingStatusDto[]> => (await tFinancialService.api.getListInvoiceFileProcessing({ batchId })).data.data,
    {
      enabled: !!enabled,
      cacheTime: 0,
      keepPreviousData: false,
      refetchInterval: refetchInterval || undefined,
    }
  );

export const useGetActivityLogs = (invoiceId: string): UseQueryResult<EntityActivityLogDto[]> =>
  useQuery(
    cacheKeysUtil.getActivityLogs(invoiceId),
    async (): Promise<EntityActivityLogDto[]> => (await tFinancialService.api.getInvoiceActivityLogs(invoiceId)).data.data,
    {
      cacheTime: 0,
      keepPreviousData: false,
    }
  );

export const useGetDomainMetaData = (): UseQueryResult<DomainMetaDataDto> =>
  useQuery(cacheKeysUtil.getDomainMetaData(), async (): Promise<DomainMetaDataDto> => (await tFinancialService.api.getDomainMetaData()).data.data, {
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh, no refetch
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    keepPreviousData: true,
  });

export const useGetCurrencies = (useType: 'DASHBOARD' | 'REPORT'): UseQueryResult<ResponseDtoListCurrencyEnum> =>
  useQuery(cacheKeysUtil.getCurrencies(), async (): Promise<ResponseDtoListCurrencyEnum> => (await tFinancialService.api.getCurrencies(useType)).data, {
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh, no refetch
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    keepPreviousData: true,
  });

export const useInvoiceSummary = (invoiceId: string, options?: { enabled?: boolean }) =>
  useQuery(cacheKeysUtil.invoiceSummary(invoiceId), async () => (await tFinancialService.api.getInvoiceSummary(invoiceId)).data.data, {
    enabled: options?.enabled !== undefined ? options.enabled : !!invoiceId,
    ...options,
  });

export const useApprovalQuestionnaire = (shipmentInvoiceId: string, enabled: boolean = false) =>
  useQuery(
    cacheKeysUtil.approvalQuestionnaire(shipmentInvoiceId),
    async () => (await tFinancialService.api.getApprovalQuestionnaire(shipmentInvoiceId)).data.data,
    {
      enabled,
    }
  );

export const useInvoiceCourierShipmentDetail = (shipmentId: string, enabled: boolean = false): UseQueryResult<InvoiceCourierShipmentDetailDto> =>
  useQuery(
    cacheKeysUtil.getShipmentDetail(shipmentId),
    async (): Promise<InvoiceCourierShipmentDetailDto> => (await tFinancialService.api.getInvoiceCourierShipmentDetail(shipmentId)).data.data,
    {
      enabled,
    }
  );

// export const useInvoiceCourierShipmentDetail = (shipmentId: string, enabled: boolean = false): UseQueryResult<InvoiceCourierShipmentDetailDto> =>
//   useQuery(
//     cacheKeysUtil.getShipmentDetail(shipmentId),
//     async (): Promise<InvoiceCourierShipmentDetailDto> => (mockInvoiceCourierShipment),
//     {
//       enabled,
//     }
//   );

export const useCourierInvoiceDisputeDetail = (invoiceDisputeId: string, enabled: boolean = true): UseQueryResult<CourierInvoiceDisputeDto> =>
  useQuery(
    cacheKeysUtil.courierInvoiceDispute(invoiceDisputeId),
    async (): Promise<CourierInvoiceDisputeDto> => (await tFinancialService.api.getCourierInvoiceDisputeDetail(invoiceDisputeId)).data.data,
    {
      enabled: !!invoiceDisputeId && enabled,
    }
  );

// export const useCourierInvoiceDisputeDetail = (invoiceDisputeId: string, enabled: boolean = true): UseQueryResult<CourierInvoiceDisputeDto> =>
//   useQuery(
//     cacheKeysUtil.courierInvoiceDispute(invoiceDisputeId),
//     async (): Promise<CourierInvoiceDisputeDto> => (mockCourierInvoiceDispute),
//     {
//       enabled,
//     }
//   );

export const useGetVersion = (invoiceId: string, enabled: boolean = false): UseQueryResult<string> =>
  useQuery(cacheKeysUtil.getVersion(invoiceId), async (): Promise<string> => (await tFinancialService.api.getVersion(invoiceId)).data.data, {
    enabled,
    cacheTime: 0,
    keepPreviousData: false,
  });

export const useUploadApprovalDocument = () =>
  useMutation(async (data: InvoiceAttachmentUploadSignedUrlRequestDto) => (await tFinancialService.api.getAttachmentPreSignedUrLs(data)).data.data);

export const useIsShipmentAsyncProcessing = () => {
  const queryClient = useQueryClient();
  return useMutation(async (shipmentInvoiceId: string) => (await tFinancialService.api.isShipmentAsyncProcessing(shipmentInvoiceId)).data.data, {
    onSuccess: async (data: boolean) => {
      if (!data) {
        await invalidateShipmentCaches(queryClient);
      }
    },
  });
};

export const useGetShipment = (shipmentInvoiceId?: string | null, options?: { enabled?: boolean }): UseQueryResult<ShipmentInActionDetailDto> =>
  useQuery(
    cacheKeysUtil.getShipment(shipmentInvoiceId),
    async (): Promise<ShipmentInActionDetailDto> => (await tFinancialService.api.getShipment(shipmentInvoiceId)).data.data,
    {
      enabled: options?.enabled !== undefined ? options.enabled : !!shipmentInvoiceId,
      cacheTime: 0,
      keepPreviousData: false,
      ...options,
    }
  );

export const useGetContractRoute = (serviceProviderId: number): UseQueryResult<RouteDto[]> =>
  useQuery(
    cacheKeysUtil.getContractRoute(serviceProviderId),
    async (): Promise<RouteDto[]> => {
      const response = await tFinancialService.api.getContractRoute({ serviceProviderId });
      return response.data.data || [];
    },
    {
      enabled: !!serviceProviderId,
      cacheTime: 0,
      keepPreviousData: false,
    }
  );

/**
 * Hook to check if an invoice is currently being processed asynchronously
 * @returns A mutation function that takes an invoiceId and returns a boolean
 */
interface ReprocessShipmentInvoiceParams {
  shipmentInvoiceId: string | string[];
  message: ShipmentMessageDto | ShipmentMessageDto[];
}

export const useIsInvoiceAsyncProcessing = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (invoiceId: string) => {
      try {
        const response = await tFinancialService.api.isInvoiceAsyncProcessing(invoiceId);
        return response.data.data;
      } catch (error) {
        console.error('Error checking if invoice is async processing:', error);
        return false;
      }
    },
    {
      onSuccess: async (_, invoiceId) => {
        // If the invoice is no longer being processed, invalidate relevant queries
        await Promise.all([
          queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceId)),
          queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceId)),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoices],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoiceStats],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getActivityLogs],
          }),
        ]);
      },
    }
  );
};

/**
 * Hook for reprocessing a shipment invoice with updated message data
 * @returns A mutation function for reprocessing shipment invoices
 */
export const useReprocessShipmentInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ shipmentInvoiceId }: ReprocessShipmentInvoiceParams) => {
      try {
        // Handle both single ID and array of IDs
        const shipmentIds = Array.isArray(shipmentInvoiceId) ? shipmentInvoiceId : [shipmentInvoiceId];
        const response = await tFinancialService.api.reProcessShipmentInvoice(shipmentIds);

        return response.data.data;
      } catch (error) {
        console.error('Error reprocessing shipment invoice:', error);
        throw error;
      }
    },
    {
      onSuccess: async () => {
        // Invalidate relevant queries to refresh data
        await Promise.all([
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoices],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoiceSummary],
          }),
        ]);
      },
    }
  );
};
